// AI Service API calls
import { API_CONFIG } from '../config/api.config';
import { createAuthenticatedApi } from './apiInterceptors';
import { notifyAiQuotaChange } from '../utils/aiQuotaEvents';

// Dedicated AI axios instance with extended timeout for AI operations
const aiApi = createAuthenticatedApi({
  timeout: API_CONFIG.TIMEOUTS.AI_OPERATIONS,
});

export interface AITodoDetail {
  title: string;
  description: string;
  ai_generated: boolean;
  success: boolean;
}

export interface AIGoalPlan {
  title: string;
  description: string;
  estimated_completion_date: string;
  sub_goals: Array<{
    title: string;
    description: string;
    todos: Array<{
      title: string;
      description: string;
      priority: string;
      /** AI estimate; every plan since 2026-10 has it */
      estimated_minutes?: number | null;
      /** YYYY-MM-DD, only when the user gave the plan a deadline */
      due_date?: string | null;
    }>;
  }>;
  success: boolean;
  message?: string;
  draft_id?: string;
}

/** Optional plan personalisation (backend services/ai_job_service.normalize_plan_preferences). */
export interface PlanPreferences {
  /** YYYY-MM-DD, after today: todos get due dates up to it */
  deadline?: string;
  hoursPerWeek?: 'lt2' | '2-5' | '5-10' | '10+';
  level?: 'beginner' | 'intermediate' | 'advanced';
  density?: 'short' | 'balanced' | 'detailed';
  note?: string;
  /** Answers to the AI's goal-specific questions ("Ask me questions") */
  answers?: PlanAnswer[];
}

export interface PlanAnswer {
  question: string;
  answer: string;
}

export interface PlanQuestion {
  question: string;
  /** A few typical answers to tap */
  options: string[];
}

const hasPreferences = (preferences?: PlanPreferences): preferences is PlanPreferences =>
  !!preferences && Object.values(preferences).some(
    (value) => (typeof value === 'string' && value.trim() !== '') || (Array.isArray(value) && value.length > 0));

/** 2-3 goal-specific questions with typical answers, asked before the plan is made. */
export const getPlanQuestions = async (message: string, preferences?: PlanPreferences): Promise<PlanQuestion[]> => {
  const response = await aiApi.post('/ai/plan-questions', {
    message,
    ...(hasPreferences(preferences) ? { preferences } : {}),
  });
  return response.data.questions || [];
};

export type AIJobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'canceled';

export interface AIGenerationJobSubmitResponse {
  job_id: string;
  status: AIJobStatus;
  progress: number;
  status_message?: string;
  poll_interval_seconds?: number;
  success: boolean;
}

export interface AIGenerationJobStatusResponse {
  job_id: string;
  status: AIJobStatus;
  progress: number;
  status_message?: string;
  result_data?: AIGoalPlan;
  error_code?: string;
  error_message?: string;
  retriable?: boolean;
  upstream_status?: number;
  draft_id?: string;
  /** While processing (Faz 8): what the AI has decided so far */
  preview?: AIPlanPreview;
  success: boolean;
}

export interface AIPlanPreview {
  goal_title?: string | null;
  sub_goals: string[];
}

export class AIGoalGenerationError extends Error {
  code?: string;
  retriable?: boolean;
  upstreamStatus?: number;

  constructor(message: string, code?: string, retriable?: boolean, upstreamStatus?: number) {
    super(message);
    this.name = 'AIGoalGenerationError';
    this.code = code;
    this.retriable = retriable;
    this.upstreamStatus = upstreamStatus;
  }
}

interface GoalPlanProgressUpdate {
  status: AIJobStatus;
  progress: number;
  statusMessage?: string;
  jobId: string;
  preview?: AIPlanPreview;
}

const POLL_INTERVAL_MS = 2000;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const generateTodoDetail = async (message: string): Promise<AITodoDetail> => {
  const response = await aiApi.post(API_CONFIG.ENDPOINTS.AI_TODO_GENERATE, {
    message
  });

  const data = response.data;
  
  if (!data.success) {
    throw new Error(data.message || 'AI servisi başarısız yanıt verdi');
  }

  return data;
};

export const submitGoalPlanJob = async (
  message: string,
  preferences?: PlanPreferences
): Promise<AIGenerationJobSubmitResponse> => {
  const response = await aiApi.post(API_CONFIG.ENDPOINTS.AI_JOB_CREATE, {
    message,
    ...(hasPreferences(preferences) ? { preferences } : {}),
  });

  const data = response.data;

  if (!data.success) {
    throw new Error(data.message || 'AI job oluşturulamadı');
  }

  return data;
};

export const getGoalPlanJobStatus = async (jobId: string): Promise<AIGenerationJobStatusResponse> => {
  const response = await aiApi.get(`${API_CONFIG.ENDPOINTS.AI_JOB_STATUS}/${jobId}`);
  const data = response.data;

  if (!data.success) {
    throw new Error(data.message || 'AI job durumu alınamadı');
  }

  return data;
};

export const generateGoalPlan = async (
  message: string,
  onProgress?: (update: GoalPlanProgressUpdate) => void,
  preferences?: PlanPreferences
): Promise<AIGoalPlan> => {
  const submitResult = await submitGoalPlanJob(message, preferences);
  const startedAt = Date.now();
  const maxWaitMs = API_CONFIG.TIMEOUTS.AI_OPERATIONS;
  const pollIntervalMs = (submitResult.poll_interval_seconds || 2) * 1000;
  const jobId = submitResult.job_id;

  onProgress?.({
    jobId,
    status: submitResult.status,
    progress: submitResult.progress || 0,
    statusMessage: submitResult.status_message,
  });

  while (Date.now() - startedAt < maxWaitMs) {
    await wait(Math.max(POLL_INTERVAL_MS, pollIntervalMs));
    const statusResult = await getGoalPlanJobStatus(jobId);

    onProgress?.({
      jobId,
      status: statusResult.status,
      progress: statusResult.progress || 0,
      statusMessage: statusResult.status_message,
      preview: statusResult.preview,
    });

    if (statusResult.status === 'completed' || statusResult.status === 'failed') {
      // A failed job gives its plan back to the daily quota
      notifyAiQuotaChange();
    }

    if (statusResult.status === 'completed') {
      if (!statusResult.result_data) {
        // No message: the modal shows its translated generic error
        throw new AIGoalGenerationError('', 'AI_EMPTY_RESULT', true);
      }

      return {
        ...statusResult.result_data,
        success: true,
        draft_id: statusResult.result_data.draft_id || statusResult.draft_id,
      };
    }

    if (statusResult.status === 'failed' || statusResult.status === 'canceled') {
      throw new AIGoalGenerationError(
        statusResult.error_message || '',
        statusResult.error_code,
        statusResult.retriable,
        statusResult.upstream_status,
      );
    }
  }

  throw new AIGoalGenerationError('AI job status polling timeout', 'AI_UPSTREAM_TIMEOUT', true);
};
